import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaShieldAlt, FaReply, FaFlag, FaBullhorn, FaShareAlt, FaEye, FaComment,
  FaBolt, FaChartLine, FaTimes, FaExclamationTriangle, FaCheckCircle,
  FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaGlobe, FaNewspaper, FaFire, FaUndo,
  FaUserPlus, FaRetweet, FaExternalLinkAlt, FaTasks, FaArrowRight, FaHeart,
  FaUsers, FaArrowUp, FaArrowDown, FaBinoculars, FaLock, FaFilter, FaFacebookF, FaTv,
} from 'react-icons/fa';
import ListsManager from '../services/ListsManager';
import AnalyticsService from '../services/AnalyticsService';
import OrgManager from '../services/OrgManager';
import ReactionModal from '../components/ReactionModal';
import ShareModal from '../components/ShareModal';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';

// ── constants ──────────────────────────────────────────────────────────────

let T = {};
let _t = (s) => s; // module-level translation ref, set on each render

const SEVERITY_COLORS = {
  critical: '#ff453a',
  high:     '#ff9f0a',
  medium:   '#ffd60a',
  low:      '#8e8e93',
};

const PLATFORM_COLORS = {
  twitter:        '#1da1f2',
  telegram:       '#0088cc',
  tiktok:         '#ff0050',
  instagram:      '#e1306c',
  facebook_group: '#1877F2',
  rss:            '#22c55e',
  webz:           '#10b981',
  news:           '#8e8e93',
  tv:             '#9f1239',
};

const PLATFORM_ICONS = {
  twitter:        FaTwitter,
  telegram:       FaTelegram,
  tiktok:         FaTiktok,
  instagram:      FaInstagram,
  facebook_group: FaFacebookF,
  rss:            FaGlobe,
  webz:           FaNewspaper,
  tv:             FaTv,
};

const EMOTION_COLORS = {
  outrage: '#ff453a', anger: '#ff453a', fear: '#bf5af2',
  mockery: '#ff9f0a', disgust: '#ff9f0a', hope: '#30d158',
  joy: '#30d158', trust: '#64d2ff', celebration: '#64d2ff',
  anticipation: '#0a84ff', surprise: '#ffd60a', concern: '#ffd60a',
  sadness: '#ac8e68', skepticism: '#ac8e68', neutral: '#8e8e93',
};

const ACTION_ICONS = {
  counter_narrative:    FaShieldAlt,
  react:                FaReply,
  report:               FaFlag,
  amplify:              FaBullhorn,
  share:                FaShareAlt,
  monitor:              FaEye,
  respond_to_comments:  FaComment,
  boost_post:           FaBolt,
  adjust_strategy:      FaChartLine,
  ignore:               FaTimes,
  engage:               FaReply,
  launch_micro_campaign: FaBullhorn,
};

const ACTION_LABELS = {
  counter_narrative:    'Counter Narrative',
  react:                'React',
  report:               'Report',
  amplify:              'Amplify',
  share:                'Share',
  monitor:              'Monitor',
  respond_to_comments:  'Respond',
  boost_post:           'Boost',
  adjust_strategy:      'Adjust Strategy',
  ignore:               'Ignore',
  engage:               'Engage',
  launch_micro_campaign: 'Launch Campaign',
};

const WS_CONFIG = {
  not_started: { labelKey: 'app.actionQueue.wsNotStarted', color: '#8e8e93', bg: 'rgba(142,142,147,0.12)', dot: '#8e8e93' },
  in_progress: { labelKey: 'app.actionQueue.wsInProgress', color: '#0a84ff', bg: 'rgba(10,132,255,0.12)',  dot: '#0a84ff' },
  done:        { labelKey: 'app.actionQueue.wsDone',        color: '#30d158', bg: 'rgba(48,209,88,0.12)',   dot: '#30d158' },
  need_help:   { labelKey: 'app.actionQueue.wsNeedHelp',   color: '#ff453a', bg: 'rgba(255,69,58,0.12)',   dot: '#ff453a' },
};

const VIOLATION_TYPE_LABELS = {
  coordinated_inauthentic_behavior: 'Coordinated Inauthentic Behavior',
  impersonation: 'Impersonation',
  synthetic_media: 'Synthetic / Manipulated Media',
  copyright_infringement: 'Copyright Infringement',
  trademark_infringement: 'Trademark Infringement',
  doxxing: 'Doxxing / Private Info',
  threats_of_violence: 'Violent Threats',
  false_factual_claims: 'False Claims',
  targeted_harassment: 'Targeted Harassment',
  hate_speech: 'Hate Speech',
};

const VIOLATION_CONFIDENCE_COLORS = {
  high:   '#ff453a',
  medium: '#ff9f0a',
  low:    '#ffd60a',
};

const PLATFORM_REPORT_INSTRUCTIONS = {
  twitter: 'Click the ··· menu on the post → "Report post" → select the matching category.',
  instagram: 'Tap the ··· menu above the post → "Report" → follow the prompts.',
  tiktok: 'Tap the Share arrow → "Report" → select the violation type.',
  telegram: 'Tap the message → "Report" — note that Telegram moderation is limited.',
  facebook_group: 'Click the ··· menu on the post → "Report post" → select the reason.',
};

const AVATAR_COLORS = ['#0055cc', '#7c3aed', '#b45309', '#065f46', '#991b1b'];

const SORT_OPTIONS = [
  { key: 'date',       label: 'Date',       defaultDir: 'desc' },
  { key: 'popularity', label: 'Popularity', defaultDir: 'desc' },
  { key: 'risk',       label: 'Brand Risk', defaultDir: 'desc' },
];

const FILTER_OPTIONS = {
  sourceType: {
    label: 'Source',
    options: [
      { value: 'twitter',   label: 'Twitter' },
      { value: 'telegram',  label: 'Telegram' },
      { value: 'tiktok',    label: 'TikTok' },
      { value: 'instagram', label: 'Instagram' },
      { value: 'rss',       label: 'RSS' },
    ],
  },
  dateRange: {
    label: 'Date Range',
    options: [
      { value: 'today', label: 'Today' },
      { value: '7d',    label: '7 days' },
      { value: '30d',   label: '30 days' },
      { value: 'all',   label: 'All time' },
    ],
  },
  sourceKind: {
    label: 'Source Kind',
    options: [
      { value: 'post',          label: 'Post' },
      { value: 'narrative',     label: 'Narrative' },
      { value: 'campaign_post', label: 'Campaign' },
    ],
  },
};

function _matchesDateRange(isoDate, range) {
  if (range === 'all' || !isoDate) return true;
  const ms = Date.now() - new Date(isoDate).getTime();
  if (range === 'today') return ms < 86400000;
  if (range === '7d')    return ms < 7 * 86400000;
  if (range === '30d')   return ms < 30 * 86400000;
  return true;
}

function chipStyle(isActive) {
  return {
    padding: '3px 8px', borderRadius: 20, fontSize: 10,
    fontWeight: isActive ? 700 : 500, cursor: 'pointer', flexShrink: 0,
    background: isActive ? `${T.primary}22` : T.surface,
    border: `1px solid ${isActive ? T.primary : T.border}`,
    color: isActive ? T.primary : T.textMuted,
  };
}

function _timeAgo(isoDate) {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmtNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return `${n}`;
}

// ── ActionQueueScreen ──────────────────────────────────────────────────────

export default function ActionQueueScreen() {
  const { theme } = useTheme();
  const { activeOrg, myRole } = useOrg();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  _t = t;

  T = {
    bg:        theme.bg,
    card:      theme.bgCard,
    surface:   theme.bgSurface,
    border:    theme.border,
    text:      theme.textPrimary,
    textSec:   theme.textSecondary,
    textMuted: theme.textMuted,
    primary:   theme.accentBlue  || '#0a84ff',
    purple:    theme.accentPurple || '#bf5af2',
    warning:   theme.accentYellow || '#ffd60a',
    success:   theme.accentGreen || '#30d158',
    error:     theme.accentRed   || '#ff453a',
    modalBg:   theme.modalBg,
    elevated:  theme.bgElevated,
    inputBg:   theme.inputBg || theme.bgSurface,
  };

  const [actions, setActions]               = useState(null);
  const [summary, setSummary]               = useState(null);
  const [loading, setLoading]               = useState(true);
  const [refreshing, setRefreshing]         = useState(false);
  const [tab, setTab]                       = useState('active');
  const [selectedId, setSelectedId]         = useState(null);
  const [assignModal, setAssignModal]       = useState(null);
  const [workStatusOpen, setWorkStatusOpen] = useState(null);
  const [members, setMembers]               = useState([]);
  const [reactionModal, setReactionModal]   = useState({ visible: false, loading: false, text: '', explanation: '', error: null, tweetId: null, author: null });
  const [currentReactionAction, setCurrentReactionAction] = useState(null);
  const [draftModal, setDraftModal]         = useState(null);
  const [feedbackModal, setFeedbackModal]   = useState(null);
  const [feedbackEff, setFeedbackEff]       = useState(3);
  const [feedbackNotes, setFeedbackNotes]   = useState('');
  const [feedbackActionTaken, setFeedbackActionTaken] = useState('react');
  const [feedbackExecution, setFeedbackExecution]     = useState('');
  const [feedbackTrack, setFeedbackTrack]             = useState(false);
  const [shareAction, setShareAction]       = useState(null);

  // ── Sort & filter state ──
  const [sortKey, setSortKey]                     = useState('date');
  const [sortDir, setSortDir]                     = useState('desc');
  const [filterActionType, setFilterActionType]   = useState(new Set());
  const [filterSourceType, setFilterSourceType]   = useState(new Set());
  const [filterDateRange,  setFilterDateRange]    = useState('all');
  const [filterSourceKind, setFilterSourceKind]   = useState(new Set());
  const [filterPlatform,   setFilterPlatform]     = useState(new Set());
  const [filterOpen, setFilterOpen]               = useState(false);

  // ── Column widths (draggable divider) ──
  const [sidebarW, setSidebarW] = useState(284);
  const dragRef     = useRef(null);
  const dragStartRef = useRef(null);

  const startDrag = (divider, e) => {
    e.preventDefault();
    dragRef.current = divider;
    dragStartRef.current = { x: e.clientX, sidebarW };
    const onMove = (mv) => {
      const rawDx = mv.clientX - dragStartRef.current.x;
      const dx = i18n.language === 'he' ? -rawDx : rawDx;
      if (dragRef.current === 'left') {
        const newSidebar = Math.max(180, Math.min(480, dragStartRef.current.sidebarW + dx));
        setSidebarW(newSidebar);
      }
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const myListenerId = activeOrg?.my_listener_id || null;

  useEffect(() => {
    if (!workStatusOpen) return;
    const close = () => setWorkStatusOpen(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [workStatusOpen]);

  const fetchActions = useCallback(async () => {
    const result = await ListsManager.fetchActionQueue(50, null, activeOrg?.id);
    if (result) {
      setActions(result.actions);
      setSummary(result.summary);
    }
    setLoading(false);
    setRefreshing(false);
  }, [activeOrg?.id]);

  useEffect(() => { fetchActions(); AnalyticsService.logActivity('view_action_queue'); }, [fetchActions]);

  useEffect(() => {
    if (!actions || selectedId) return;
    const first = actions.find(a => a.status === 'open' || a.status === 'in_progress');
    if (first) setSelectedId(first.id);
  }, [actions, selectedId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await ListsManager.refreshActionQueue(activeOrg?.id);
    await fetchActions();
  };

  const handleDismiss = async (actionId) => {
    await ListsManager.updateActionStatus(actionId, 'dismissed', null, null, activeOrg?.id);
    setActions(prev => prev ? prev.filter(a => a.id !== actionId) : prev);
    setSummary(prev => prev ? { ...prev, total: Math.max(0, prev.total - 1) } : prev);
    if (selectedId === actionId) setSelectedId(null);
  };

  const handleClaim = async (actionId) => {
    if (!activeOrg?.id) return;
    await ListsManager.claimAction(actionId, activeOrg.id);
    const me = members.find(m => m.listener_id === myListenerId);
    setActions(prev => prev ? prev.map(a => a.id !== actionId ? a : {
      ...a, status: 'in_progress', can_claim: false, can_mark_done: true,
      assignees: [...(a.assignees || []).filter(x => x.listener_id !== myListenerId),
        { listener_id: myListenerId || 'me', username: me?.username || 'you', status: 'in_progress' }],
    }) : prev);
  };

  const handleOpenAssign = async (actionId, e) => {
    e.stopPropagation();
    if (!activeOrg?.id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (members.length === 0) {
      const result = await OrgManager.listMembers(activeOrg.id);
      if (result) setMembers(result);
    }
    setAssignModal({ actionId, top: rect.bottom + 8, right: window.innerWidth - rect.right });
  };

  const handleAssign = async (listenerId) => {
    if (!assignModal || !activeOrg?.id) return;
    const { actionId } = assignModal;
    const action = (actions || []).find(a => a.id === actionId);
    if (action?.assignees?.some(x => x.listener_id === listenerId)) return;
    if (listenerId === myListenerId) { setAssignModal(null); await handleClaim(actionId); return; }
    setAssignModal(null);
    await ListsManager.assignAction(actionId, activeOrg.id, listenerId);
    const member = members.find(m => m.listener_id === listenerId);
    setActions(prev => prev ? prev.map(a => a.id !== actionId ? a : {
      ...a, status: 'in_progress',
      assignees: [...(a.assignees || []), { listener_id: listenerId, username: member?.username || '', status: 'in_progress' }],
      can_claim: false,
    }) : prev);
  };

  const handleUnassign = async (actionId, listenerId) => {
    if (!activeOrg?.id) return;
    setAssignModal(null);
    await ListsManager.unassignAction(actionId, listenerId, activeOrg.id);
    setActions(prev => prev ? prev.map(a => a.id !== actionId ? a : {
      ...a,
      assignees: (a.assignees || []).filter(x => x.listener_id !== listenerId),
      can_claim: a.assignees?.length <= 1 ? (myRole === 'owner' || myRole === 'admin' || myRole === 'member') : a.can_claim,
      status: a.assignees?.length <= 1 ? 'open' : a.status,
    }) : prev);
  };

  const handleReopen = async (actionId) => {
    if (!activeOrg?.id) return;
    setActions(prev => prev ? prev.map(a => a.id !== actionId ? a : {
      ...a, status: 'open', work_status: 'not_started',
      can_claim: true, can_assign: true, can_mark_done: true, can_dismiss: true,
    }) : prev);
    await ListsManager.updateActionStatus(actionId, 'open', null, null, activeOrg?.id);
  };

  const handleMarkAsDone = (actionId) => {
    setFeedbackModal({ actionId });
    setFeedbackEff(3);
    setFeedbackNotes('');
    setFeedbackActionTaken('react');
    setFeedbackExecution('');
    setFeedbackTrack(false);
  };

  const handleFeedbackSkip = async () => {
    if (!feedbackModal) return;
    const actionId = feedbackModal.actionId;
    setFeedbackModal(null);
    await ListsManager.updateActionStatus(actionId, 'acted', null, null, activeOrg?.id);
    setActions(prev => prev ? prev.map(a => a.id !== actionId ? a : { ...a, status: 'acted', can_claim: false, can_assign: false, can_mark_done: false, can_dismiss: false }) : prev);
  };

  const handleFeedbackSave = async () => {
    if (!feedbackModal) return;
    const actionId = feedbackModal.actionId;
    const shouldTrack = feedbackTrack;
    const action = (actions || []).find(a => a.id === actionId);
    setFeedbackModal(null);
    await ListsManager.updateActionStatus(actionId, 'acted', feedbackEff, feedbackNotes || null, activeOrg?.id, feedbackActionTaken || null, feedbackExecution || null);
    setActions(prev => prev ? prev.map(a => a.id !== actionId ? a : { ...a, status: 'acted', is_tracked: shouldTrack || a.is_tracked, can_claim: false, can_assign: false, can_mark_done: false, can_dismiss: false }) : prev);
    if (shouldTrack && action) {
      try {
        await ListsManager.createTracker({
          tracker_type: (action.recommended_action || '').toLowerCase().includes('report') ? 'content_report' : 'action_outcome',
          action_queue_id: actionId,
          narrative_id: action.narrative_id || undefined,
          source_post_id: action.source_post_id || undefined,
          source_platform: action.source_type || undefined,
          source_url: action.source_url || undefined,
          title: `Tracking: ${(action.title || 'Action').slice(0, 80)}`,
          org_id: activeOrg?.id || action.org_id || undefined,
        });
      } catch (e) { console.error('Failed to create tracker from feedback:', e); }
    }
  };

  const handleWorkStatusChange = async (actionId, workStatus) => {
    const orgId = activeOrg?.id || (actions || []).find(a => a.id === actionId)?.org_id;
    if (!orgId) return;
    setWorkStatusOpen(null);
    setActions(prev => prev ? prev.map(a => a.id !== actionId ? a : { ...a, work_status: workStatus }) : prev);
    await ListsManager.updateWorkStatus(actionId, workStatus, orgId);
    if (workStatus === 'done') { setFeedbackModal({ actionId }); setFeedbackEff(3); setFeedbackNotes(''); setFeedbackTrack(false); }
  };

  const _buildGuidance = (action) => {
    if (!action.reasoning && !(action.suggested_steps?.length > 0)) return null;
    const parts = [];
    if (action.reasoning) parts.push(`Situation: ${action.reasoning}`);
    if (action.suggested_steps?.length > 0) {
      parts.push('Recommended steps:');
      action.suggested_steps.forEach((step, i) => parts.push(`${i + 1}. ${step}`));
    }
    return parts.join('\n');
  };

  const handleDraftReaction = async (action, e) => {
    if (e) e.stopPropagation();
    const isTwitterPost = action.source_kind === 'post' && action.source_type === 'twitter';
    const guidance = _buildGuidance(action);
    if (isTwitterPost) {
      const tweetId = action.source_url ? (action.source_url.match(/\/status\/(\d+)/) || [])[1] : null;
      setCurrentReactionAction(action);
      setReactionModal({ visible: true, loading: true, text: '', explanation: '', error: null, tweetId, author: action.post_author });
      try {
        const result = await ListsManager.generateTweetReaction(action.post_content || '', action.post_author || '', null, null, null, guidance);
        setReactionModal(prev => ({ ...prev, loading: false, text: result?.reaction_text || '', explanation: result?.explanation || '', error: result?.success === false ? (result.error_message || 'Failed') : null }));
      } catch { setReactionModal(prev => ({ ...prev, loading: false, error: 'Failed to generate draft' })); }
    } else {
      const title = action.source_kind === 'post' ? 'Draft Reply' : 'Draft Post';
      setDraftModal({ loading: true, text: null, error: null, title });
      try {
        const result = action.source_kind === 'post'
          ? await ListsManager.generateTweetReaction(action.post_content || '', action.post_author || '', null, null, null, guidance)
          : await ListsManager.generateNarrativeReaction(action.narrative_id, null, guidance);
        setDraftModal({ loading: false, text: result?.reaction_text || null, error: result?.success === false ? (result.error_message || 'Failed') : null, title });
      } catch { setDraftModal({ loading: false, text: null, error: 'Failed to generate draft', title }); }
    }
  };

  const actionTypeOptions = [...new Set((actions || []).map(a => a.recommended_action).filter(Boolean))]
    .filter(v => v !== 'ignore')
    .map(v => ({ value: v, label: ACTION_LABELS[v] || v }));

  const activeActions    = (actions || []).filter(a =>
    a.recommended_action !== 'ignore'
    && (a.status === 'open' || a.status === 'in_progress')
    && !(a.violation_detected && a.recommended_action === 'report' && a.source_kind === 'violation')
  );
  const violationActions = (actions || []).filter(a =>
    a.recommended_action !== 'ignore'
    && a.violation_detected && (a.status === 'open' || a.status === 'in_progress')
  );
  const completedActions = (actions || []).filter(a => a.recommended_action !== 'ignore' && a.status === 'acted');

  const toggleSetFilter = (setter, value) =>
    setter(prev => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });

  const resetFilters = () => {
    setFilterActionType(new Set());
    setFilterSourceType(new Set());
    setFilterDateRange('all');
    setFilterSourceKind(new Set());
    setFilterPlatform(new Set());
  };

  const activeFilterCount =
    filterActionType.size + filterSourceType.size + filterSourceKind.size +
    filterPlatform.size + (filterDateRange !== 'all' ? 1 : 0);

  const applyFilters = (arr) => arr.filter(a => {
    if (filterActionType.size > 0 && !filterActionType.has(a.recommended_action)) return false;
    if (filterSourceType.size > 0 && !filterSourceType.has(a.source_type))        return false;
    if (filterSourceKind.size > 0 && !filterSourceKind.has(a.source_kind))        return false;
    if (filterPlatform.size > 0 && a.source_kind === 'post' && !filterPlatform.has(a.source_type)) return false;
    if (!_matchesDateRange(a.created_at, filterDateRange))                         return false;
    return true;
  });

  const applySort = (arr) => {
    return [...arr].sort((a, b) => {
      let valA, valB;
      if (sortKey === 'date') {
        valA = new Date(a.created_at || 0).getTime();
        valB = new Date(b.created_at || 0).getTime();
      } else if (sortKey === 'popularity') {
        const eng = (x) =>
          Math.log1p(x.message_count || 0) * 2 +
          (x.velocity_1h || 0) * 5 +
          Math.log1p(x.post_author_followers || 0) * 1.5;
        valA = eng(a);
        valB = eng(b);
      } else {
        const risk = (x) => {
          const sentimentRisk = (-(x.sentiment_score ?? 0) + 1) / 2;
          const reachNorm     = Math.min(Math.log1p(x.post_author_followers || 0) / 15, 1);
          const velocityNorm  = Math.min((x.velocity_1h || 0) / 100, 1);
          const volumeNorm    = Math.min(Math.log1p(x.message_count || 0) / 10, 1);
          return sentimentRisk * 0.4 + reachNorm * 0.3 + velocityNorm * 0.2 + volumeNorm * 0.1;
        };
        valA = risk(a);
        valB = risk(b);
      }
      return sortDir === 'desc' ? valB - valA : valA - valB;
    });
  };

  const listItems = applySort(applyFilters(
    tab === 'active'     ? activeActions :
    tab === 'violations' ? violationActions :
                           completedActions
  ));
  const selectedAction   = selectedId ? (actions || []).find(a => a.id === selectedId) : null;
  const criticalCount    = summary?.critical || 0;
  const violationCount   = summary?.violations || violationActions.length;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: T.bg }}>
        <ActivityIndicator size="large" color="#0a84ff" />
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .aq-item { transition: background 0.12s; cursor: pointer; }
        .aq-item:hover { background: ${T.surface} !important; }
        .aq-item.sel { background: rgba(10,132,255,0.08) !important; }
        .aq-btn { transition: opacity 0.12s; cursor: pointer; }
        .aq-btn:hover { opacity: 0.8; }
        .aq-tab { cursor: pointer; transition: all 0.12s; }
        .aq-cta { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; transition: filter 0.12s; }
        .aq-cta:hover { filter: brightness(1.12); }
        .aq-smp-card { transition: transform 0.15s, box-shadow 0.15s; cursor: pointer; border-radius: 10px; overflow: hidden; }
        .aq-smp-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.35); }
        .aq-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; position: relative; z-index: 10; transition: background 0.15s; }
        .aq-divider:hover, .aq-divider:active { background: ${T.primary}55; }
        .aq-divider::after { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 1px; height: 32px; background: ${T.border}; border-radius: 1px; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.2); border-radius: 2px; }
        .aq-tiktok-wrap iframe { display: block; }
      ` }} />

      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: T.bg, overflow: 'hidden' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 24px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(10,132,255,0.12)', border: '1px solid rgba(10,132,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FaTasks size={16} color="#0a84ff" />
          </div>
          <div>
            <div style={{ color: T.text, fontSize: 20, fontWeight: 700, letterSpacing: -0.4 }}>{t('app.actionQueue.title')}</div>
            <div style={{ color: T.textMuted, fontSize: 12, marginTop: 1 }}>
              {criticalCount > 0 && <span style={{ color: SEVERITY_COLORS.critical }}>{criticalCount} {t('app.actionQueue.statusCritical')} · </span>}
              {violationCount > 0 && <span style={{ color: '#ff453a' }}>{violationCount} {t('app.actionQueue.statusToReport')} · </span>}
              {activeActions.length} {t('app.actionQueue.statusActive')}
              {completedActions.length > 0 ? ` · ${completedActions.length} ${t('app.actionQueue.statusCompleted')}` : ''}
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button className="aq-btn" onClick={handleRefresh} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, background: T.surface, border: `1px solid ${T.border}`, color: T.textSec, fontSize: 12, fontWeight: 600 }}>
              {refreshing ? <ActivityIndicator size="small" color={T.primary} /> : <FaUndo size={11} color={T.textMuted} />}
              <span>{t('app.actionQueue.refresh')}</span>
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* ── LEFT sidebar ── */}
          <div style={{ width: sidebarW, flexShrink: 0, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', padding: '12px 12px 0', gap: 4, flexShrink: 0 }}>
              {[
                { key: 'active', label: `${t('app.actionQueue.tabActive')} (${activeActions.length})` },
                ...(violationCount > 0 ? [{ key: 'violations', label: `${t('app.actionQueue.tabToReport')} (${violationActions.length})`, accent: '#ff453a' }] : []),
                { key: 'completed', label: `${t('app.actionQueue.tabDone')} (${completedActions.length})` },
              ].map(tabItem => (
                <div key={tabItem.key} className="aq-tab" onClick={() => setTab(tabItem.key)} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, color: tab === tabItem.key ? (tabItem.accent || T.primary) : T.textMuted, background: tab === tabItem.key ? `${tabItem.accent || T.primary}18` : 'transparent' }}>
                  {tabItem.label}
                </div>
              ))}
            </div>

            {/* ── Sort + Filter bar ── */}
            <div style={{ flexShrink: 0, borderBottom: `1px solid ${T.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px', overflowX: 'auto' }}>
                {SORT_OPTIONS.map(opt => {
                  const isActive = sortKey === opt.key;
                  let chipColor = T.primary;
                  if (isActive) {
                    if (opt.key === 'popularity') {
                      chipColor = sortDir === 'desc' ? '#30d158' : '#ff453a';
                    } else if (opt.key === 'risk') {
                      chipColor = sortDir === 'desc' ? '#30d158' : '#ff453a';
                    } else if (opt.key === 'date') {
                      chipColor = sortDir === 'desc' ? '#30d158' : '#ff453a';
                    }
                  }
                  return (
                    <div key={opt.key} className="aq-btn"
                      onClick={() => {
                        if (isActive) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                        else { setSortKey(opt.key); setSortDir(opt.defaultDir); }
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0, background: isActive ? `${chipColor}20` : T.surface, border: `1px solid ${isActive ? chipColor : T.border}`, color: isActive ? chipColor : T.textMuted }}>
                      {opt.label}
                      {isActive && (sortDir === 'desc' ? <FaArrowDown size={8} color={chipColor} /> : <FaArrowUp size={8} color={chipColor} />)}
                    </div>
                  );
                })}
                <div style={{ flex: 1 }} />
                <div className="aq-btn" onClick={() => setFilterOpen(p => !p)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0, background: (filterOpen || activeFilterCount > 0) ? `${T.primary}20` : T.surface, border: `1px solid ${(filterOpen || activeFilterCount > 0) ? T.primary : T.border}`, color: (filterOpen || activeFilterCount > 0) ? T.primary : T.textMuted }}>
                  <FaFilter size={9} />
                  {activeFilterCount > 0 && (
                    <span style={{ minWidth: 14, height: 14, borderRadius: 7, background: T.primary, color: '#fff', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>{activeFilterCount}</span>
                  )}
                </div>
              </div>
              {filterOpen && (
                <div style={{ padding: '0 8px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {activeFilterCount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <span onClick={resetFilters} className="aq-btn" style={{ color: T.primary, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>{t('app.actionQueue.resetAll')}</span>
                    </div>
                  )}
                  {actionTypeOptions.length > 0 && <FilterChipGroup label={t('app.actionQueue.filterActionType')} options={actionTypeOptions} selected={filterActionType} onToggle={(v) => toggleSetFilter(setFilterActionType, v)} />}
                  <FilterChipGroup label={t('app.actionQueue.filterSourceKind')} options={FILTER_OPTIONS.sourceKind.options} selected={filterSourceKind} onToggle={(v) => toggleSetFilter(setFilterSourceKind, v)} />
                  {filterSourceKind.has('post') && (
                    <FilterChipGroup label={t('app.actionQueue.filterPlatform')} options={FILTER_OPTIONS.sourceType.options} selected={filterPlatform} onToggle={(v) => toggleSetFilter(setFilterPlatform, v)} />
                  )}
                  <div>
                    <div style={{ color: T.textMuted, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{t('app.actionQueue.filterDateRange')}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {FILTER_OPTIONS.dateRange.options.map(opt => (
                        <div key={opt.value} onClick={() => setFilterDateRange(opt.value)} className="aq-btn" style={chipStyle(filterDateRange === opt.value)}>{opt.label}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
              {listItems.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 8 }}>
                  <FaCheckCircle size={28} color="#30d158" />
                  <span style={{ color: T.textMuted, fontSize: 13 }}>{activeFilterCount > 0 ? t('app.actionQueue.emptyNoMatches') : tab === 'active' ? t('app.actionQueue.emptyAllClear') : tab === 'violations' ? t('app.actionQueue.emptyNoViolations') : t('app.actionQueue.emptyNothingYet')}</span>
                </div>
              )}
              {listItems.map(action => <ListItem key={action.id} action={action} isSelected={selectedId === action.id} onSelect={() => setSelectedId(action.id)} />)}
            </div>
          </div>

          <div className="aq-divider" onMouseDown={(e) => startDrag('left', e)} />

          {/* ── CENTER + RIGHT ── */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
            {!selectedAction ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                <FaTasks size={40} color={T.border} />
                <span style={{ color: T.textMuted, fontSize: 14 }}>{t('app.actionQueue.selectAction')}</span>
              </div>
            ) : (
              <ActionDetail
                action={selectedAction}
                actions={actions}
                members={members}
                myListenerId={myListenerId}
                myRole={myRole}
                activeOrg={activeOrg}
                workStatusOpen={workStatusOpen}
                setWorkStatusOpen={setWorkStatusOpen}
                onDismiss={handleDismiss}
                onClaim={handleClaim}
                onOpenAssign={handleOpenAssign}
                onUnassign={handleUnassign}
                onMarkDone={handleMarkAsDone}
                onReopen={handleReopen}
                onDraft={handleDraftReaction}
                onShare={(action) => setShareAction(action)}
                onWorkStatusChange={handleWorkStatusChange}
                navigate={navigate}
                T={T}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Assign popover ── */}
      {assignModal && ReactDOM.createPortal(
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setAssignModal(null)} />
          <div style={{ position: 'fixed', top: assignModal.top, right: assignModal.right, zIndex: 9999, background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.35)', minWidth: 224, maxWidth: 260, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px 8px', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('app.actionQueue.assignees')}</span>
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {(() => {
                const modalAction = (actions || []).find(a => a.id === assignModal.actionId);
                const assignedIds = new Set((modalAction?.assignees || []).map(x => x.listener_id));
                return members.map(m => {
                  const isAssigned = assignedIds.has(m.listener_id);
                  const isSelf = m.listener_id === myListenerId;
                  const colorIdx = m.username ? m.username.charCodeAt(0) % AVATAR_COLORS.length : 0;
                  const canRemove = isAssigned && (isSelf || myRole === 'owner' || myRole === 'admin');
                  const onClick = isAssigned ? (canRemove ? () => handleUnassign(assignModal.actionId, m.listener_id) : undefined) : () => handleAssign(m.listener_id);
                  return (
                    <div key={m.listener_id} onClick={onClick}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: onClick ? 'pointer' : 'default', opacity: isAssigned && !canRemove ? 0.5 : 1 }}
                      onMouseEnter={onClick ? e => { e.currentTarget.style.background = T.surface; } : undefined}
                      onMouseLeave={onClick ? e => { e.currentTarget.style.background = 'transparent'; } : undefined}>
                      <div style={{ width: 30, height: 30, borderRadius: 15, background: isAssigned ? AVATAR_COLORS[colorIdx] : T.surface, border: isAssigned ? 'none' : `1.5px dashed ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isAssigned ? '#fff' : T.textMuted, fontSize: 12, fontWeight: 700 }}>
                        {(m.username || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ color: T.text, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.username?.split('@')[0]}{isSelf ? ` (${t('app.actionQueue.you')})` : ''}</div>
                        <div style={{ color: T.textMuted, fontSize: 10, textTransform: 'capitalize' }}>{m.role}</div>
                      </div>
                      {isAssigned
                        ? <div style={{ width: 18, height: 18, borderRadius: 9, background: '#30d158', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: '#fff', fontSize: 10, fontWeight: 800 }}>✓</span></div>
                        : <div style={{ width: 18, height: 18, borderRadius: 9, border: `1.5px solid ${T.border}` }} />}
                    </div>
                  );
                });
              })()}
            </div>
            <div style={{ padding: '6px 14px 8px', borderTop: `1px solid ${T.border}` }}>
              <span style={{ color: T.textMuted, fontSize: 10 }}>{t('app.actionQueue.assignHint')}</span>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* ── Reaction modal ── */}
      <ReactionModal
        isVisible={reactionModal.visible}
        onClose={() => setReactionModal(prev => ({ ...prev, visible: false }))}
        reactionText={reactionModal.text}
        explanation={reactionModal.explanation}
        isLoading={reactionModal.loading}
        onRegenerate={() => { if (currentReactionAction) handleDraftReaction(currentReactionAction, null); }}
        platform="twitter"
        originalAuthor={reactionModal.author}
        tweetId={reactionModal.tweetId}
        error={reactionModal.error}
      />

      {/* ── Draft modal ── */}
      {draftModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setDraftModal(null)}>
          <div style={{ background: T.modalBg, borderRadius: 24, padding: 28, width: 380, maxWidth: 'calc(100vw - 32px)', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ color: T.text, fontSize: 18, fontWeight: 700 }}>{draftModal.title}</span>
              <button onClick={() => setDraftModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><FaTimes size={14} color={T.textSec} /></button>
            </div>
            {draftModal.loading && <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0' }}><ActivityIndicator size="large" color={T.primary} /><span style={{ color: T.textSec, fontSize: 13, marginTop: 12 }}>{t('app.actionQueue.drafting')}</span></div>}
            {!draftModal.loading && draftModal.error && <span style={{ color: '#ff453a', fontSize: 13, textAlign: 'center', display: 'block', padding: '16px 0' }}>{draftModal.error}</span>}
            {!draftModal.loading && draftModal.text && (
              <>
                <div style={{ background: T.surface, borderRadius: 12, padding: 16, marginBottom: 16, border: `1px solid ${T.border}`, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: T.text, fontSize: 14, lineHeight: 1.6, maxHeight: 260, overflowY: 'auto', userSelect: 'text' }}>{draftModal.text}</div>
                <button onClick={() => navigator.clipboard.writeText(draftModal.text)} style={{ width: '100%', padding: '12px 0', borderRadius: 12, border: 'none', background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>{t('app.actionQueue.copyToClipboard')}</button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ── Feedback modal ── */}
      {feedbackModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99998, overflowY: 'auto', padding: 20 }} onClick={handleFeedbackSkip}>
          <div style={{ background: T.modalBg, borderRadius: 24, padding: 28, width: '100%', maxWidth: 480, border: `1px solid ${T.border}`, boxSizing: 'border-box' }} onClick={e => e.stopPropagation()}>
            <p style={{ color: T.text, fontSize: 18, fontWeight: 700, marginBottom: 4, textAlign: 'center' }}>{t('app.actionQueue.feedbackTitle')} 🎉</p>
            <p style={{ color: T.textSec, fontSize: 12, marginBottom: 24, textAlign: 'center' }}>{t('app.actionQueue.feedbackSubtitle')}</p>
            <p style={{ color: T.textSec, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{t('app.actionQueue.feedbackHowWell')}</p>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 22 }}>
              {[{ v: 1, e: '😞', l: t('app.actionQueue.feedbackPoor') }, { v: 2, e: '😐', l: t('app.actionQueue.feedbackMeh') }, { v: 3, e: '🤷', l: t('app.actionQueue.feedbackOk') }, { v: 4, e: '😊', l: t('app.actionQueue.feedbackGood') }, { v: 5, e: '🎉', l: t('app.actionQueue.feedbackGreat') }].map(item => (
                <div key={item.v} onClick={() => setFeedbackEff(item.v)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 8, borderRadius: 14, minWidth: 52, background: feedbackEff === item.v ? `${T.primary}25` : 'transparent', border: feedbackEff === item.v ? `1.5px solid ${T.primary}` : '1.5px solid transparent', cursor: 'pointer' }}>
                  <span style={{ fontSize: 28 }}>{item.e}</span>
                  <span style={{ color: feedbackEff === item.v ? T.primary : T.textMuted, fontSize: 10, marginTop: 4 }}>{item.l}</span>
                </div>
              ))}
            </div>
            <p style={{ color: T.textSec, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('app.actionQueue.feedbackWhatDidYouDo')}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {[{ value: 'react', label: t('app.actionQueue.feedbackActionReact') }, { value: 'counter_narrative', label: t('app.actionQueue.feedbackActionCounter') }, { value: 'amplify', label: t('app.actionQueue.feedbackActionAmplify') }, { value: 'share', label: t('app.actionQueue.feedbackActionShare') }, { value: 'report', label: t('app.actionQueue.feedbackActionReport') }, { value: 'monitor', label: t('app.actionQueue.feedbackActionMonitor') }, { value: 'respond_to_comments', label: t('app.actionQueue.feedbackActionRespond') }, { value: 'boost_post', label: t('app.actionQueue.feedbackActionBoost') }, { value: 'adjust_strategy', label: t('app.actionQueue.feedbackActionAdjust') }].map(at => (
                <div key={at.value} onClick={() => setFeedbackActionTaken(at.value)} style={{ padding: '6px 12px', borderRadius: 20, cursor: 'pointer', background: feedbackActionTaken === at.value ? `${T.primary}30` : T.surface, border: `1px solid ${feedbackActionTaken === at.value ? T.primary : T.border}`, color: feedbackActionTaken === at.value ? T.primary : T.textSec, fontSize: 12, fontWeight: feedbackActionTaken === at.value ? 600 : 400 }}>{at.label}</div>
              ))}
            </div>
            <p style={{ color: T.textSec, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('app.actionQueue.feedbackWhatSteps')}</p>
            <textarea value={feedbackExecution} onChange={e => setFeedbackExecution(e.target.value)} placeholder="e.g. Posted a public response..." rows={2} style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12, color: T.text, fontSize: 13, resize: 'none', marginBottom: 16, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
            <p style={{ color: T.textSec, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('app.actionQueue.feedbackWhatResults')}</p>
            <textarea value={feedbackNotes} onChange={e => setFeedbackNotes(e.target.value)} placeholder="e.g. Sentiment improved 20%..." rows={2} style={{ width: '100%', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12, color: T.text, fontSize: 13, resize: 'none', marginBottom: 16, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
            <div onClick={() => setFeedbackTrack(!feedbackTrack)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, background: feedbackTrack ? `rgba(10,132,255,0.08)` : T.surface, border: `1px solid ${feedbackTrack ? 'rgba(10,132,255,0.4)' : T.border}`, cursor: 'pointer', marginBottom: 20, transition: 'all 0.2s' }}>
              <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${feedbackTrack ? T.primary : T.border}`, background: feedbackTrack ? T.primary : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                {feedbackTrack && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800, lineHeight: 1 }}>✓</span>}
              </div>
              <div>
                <span style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>{t('app.actionQueue.feedbackTrackOutcome')}</span>
                <p style={{ margin: '2px 0 0', color: T.textMuted, fontSize: 11 }}>{t('app.actionQueue.feedbackTrackDesc')}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleFeedbackSkip} style={{ flex: 1, padding: '13px 0', borderRadius: 12, background: T.surface, border: `1px solid ${T.border}`, color: T.textSec, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('app.actionQueue.skip')}</button>
              <button onClick={handleFeedbackSave} style={{ flex: 2, padding: '13px 0', borderRadius: 12, background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('app.actionQueue.saveDone')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {shareAction && (
        <ShareModal
          type="action" id={shareAction.id} title={shareAction.title}
          summary={shareAction.context_summary || shareAction.reasoning || ''}
          onClose={() => setShareAction(null)}
        />
      )}
    </>
  );
}

// ── FilterChipGroup ────────────────────────────────────────────────────────

function FilterChipGroup({ label, options, selected, onToggle }) {
  return (
    <div>
      <div style={{ color: T.textMuted, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {options.map(opt => (
          <div key={opt.value} onClick={() => onToggle(opt.value)} className="aq-btn" style={chipStyle(selected.has(opt.value))}>{opt.label}</div>
        ))}
      </div>
    </div>
  );
}

// ── ListItem ───────────────────────────────────────────────────────────────

function ListItem({ action, isSelected, onSelect }) {
  const IconComp = ACTION_ICONS[action.recommended_action] || FaExclamationTriangle;
  const sevColor = SEVERITY_COLORS[action.severity] || '#8e8e93';
  const platIcon = PLATFORM_ICONS[action.source_type];
  const platColor = PLATFORM_COLORS[action.source_type] || '#8e8e93';
  const isCompleted = action.status === 'acted';
  const ws = action.work_status || 'not_started';
  const wsConf = WS_CONFIG[ws] || WS_CONFIG.not_started;
  const accent = isCompleted ? '#30d158' : sevColor;

  return (
    <div
      className={`aq-item${isSelected ? ' sel' : ''}`}
      onClick={onSelect}
      style={{ borderRadius: 10, borderLeft: `3px solid ${accent}`, marginBottom: 4, padding: '10px 12px', background: isSelected ? 'rgba(10,132,255,0.08)' : T.card, position: 'relative' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 5 }}>
        <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
          <IconComp size={11} color={accent} />
        </div>
        <span style={{ color: isCompleted ? T.textSec : T.text, fontSize: 12, fontWeight: 600, lineHeight: '16px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {action.title}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: accent, background: `${accent}15`, padding: '2px 5px', borderRadius: 4 }}>
          {ACTION_LABELS[action.recommended_action] || action.recommended_action}
        </span>
        {action.violation_detected && (
          <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: '#ff453a', background: 'rgba(255,69,58,0.12)', padding: '2px 5px', borderRadius: 4 }}>
            {_t('app.actionQueue.violation')}
          </span>
        )}
        {!isCompleted && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: wsConf.color }}>
            <span style={{ width: 5, height: 5, borderRadius: 3, background: wsConf.dot, display: 'inline-block' }} />
            {_t(wsConf.labelKey)}
          </span>
        )}
        {platIcon && React.createElement(platIcon, { size: 10, color: platColor })}
        <span style={{ color: T.textMuted, fontSize: 10, marginLeft: 'auto' }}>{action.created_at ? _timeAgo(action.created_at) : ''}</span>
      </div>
      {action.assignees?.length > 0 && (
        <div style={{ display: 'flex', marginTop: 6 }}>
          {action.assignees.slice(0, 4).map((a, i) => (
            <div key={a.listener_id} style={{ width: 18, height: 18, borderRadius: 9, background: AVATAR_COLORS[a.username ? a.username.charCodeAt(0) % AVATAR_COLORS.length : 0], display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: i > 0 ? -5 : 0, border: `1.5px solid ${T.card}`, zIndex: 5 - i, position: 'relative' }}>
              <span style={{ color: '#fff', fontSize: 7, fontWeight: 800 }}>{(a.username || '?')[0].toUpperCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ActionDetail ───────────────────────────────────────────────────────────

function ActionDetail({ action, actions, members, myListenerId, myRole, activeOrg, workStatusOpen, setWorkStatusOpen, onDismiss, onClaim, onOpenAssign, onUnassign, onMarkDone, onReopen, onDraft, onShare, onWorkStatusChange, navigate, T }) {
  const t = _t;
  const [trackingState, setTrackingState] = useState(action.is_tracked ? 'done' : 'idle'); // idle | loading | done | error
  useEffect(() => { setTrackingState(action.is_tracked ? 'done' : 'idle'); }, [action.id, action.is_tracked]);
  const IconComp   = ACTION_ICONS[action.recommended_action] || FaExclamationTriangle;
  const sevColor   = SEVERITY_COLORS[action.severity] || '#8e8e93';
  const actionLabel = ACTION_LABELS[action.recommended_action] || action.recommended_action;
  const PlatformIcon = PLATFORM_ICONS[action.source_type];
  const platColor  = PLATFORM_COLORS[action.source_type] || '#8e8e93';
  const timeAgo    = action.created_at ? _timeAgo(action.created_at) : '';
  const isCompleted = action.status === 'acted';
  const ws         = action.work_status || 'not_started';
  const wsConf     = WS_CONFIG[ws] || WS_CONFIG.not_started;
  const isWsOpen   = workStatusOpen?.id === action.id;
  const tweetId    = action.source_url ? (action.source_url.match(/\/status\/(\d+)/) || [])[1] : null;
  const isTikTok   = action.source_type === 'tiktok';
  const isTwitter  = action.source_type === 'twitter';
  const isTelegram = action.source_type === 'telegram';
  const isNarrative = action.source_kind === 'narrative';
  const isPost     = action.source_kind === 'post';

  const tiktokId = action.source_url ? (action.source_url.match(/video\/(\d+)/) || [])[1] : null;

  // ── CTAs ──
  let navCTA = null;
  if (action.source_url) {
    navCTA = { label: t('app.actionQueue.ctaViewPost'), icon: PlatformIcon || FaEye, color: platColor, onPress: () => window.open(action.source_url, '_blank') };
  } else if (isNarrative && action.narrative_id) {
    navCTA = { label: t('app.actionQueue.ctaViewNarrative'), icon: FaFire, color: T.warning, onPress: () => navigate(`/narratives/${action.narrative_id}`) };
  } else if (action.source_kind === 'campaign_post' && action.campaign_id) {
    navCTA = { label: t('app.actionQueue.ctaViewCampaign'), icon: FaBullhorn, color: T.purple, onPress: () => navigate(`/campaigns/${action.campaign_id}`) };
  }

  let primaryCTA = null, secondaryCTA = null;
  if (action.recommended_action === 'amplify' && isTwitter && tweetId) {
    primaryCTA = { label: t('app.actionQueue.ctaRetweet'), icon: FaRetweet, color: '#30d158', onPress: () => window.open(`https://twitter.com/intent/retweet?tweet_id=${tweetId}`, '_blank') };
    secondaryCTA = navCTA;
  } else if (action.recommended_action === 'amplify') {
    primaryCTA = navCTA;
  } else if (action.recommended_action === 'share' && isTwitter && action.source_url) {
    primaryCTA = { label: t('app.actionQueue.ctaQuoteTweet'), icon: FaShareAlt, color: '#30d158', onPress: () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(action.source_url)}`, '_blank') };
    secondaryCTA = navCTA;
  } else if (action.recommended_action === 'share' && isTelegram && action.source_url) {
    primaryCTA = { label: t('app.actionQueue.ctaForward'), icon: FaShareAlt, color: '#0088cc', onPress: () => window.open(`https://t.me/share/url?url=${encodeURIComponent(action.source_url)}`, '_blank') };
    secondaryCTA = navCTA;
  } else if (action.recommended_action === 'share') {
    primaryCTA = navCTA;
  } else if (action.recommended_action === 'report' && (action.report_url || action.source_url)) {
    const reportTarget = action.report_url || action.source_url;
    const isIp = action.violation_type === 'copyright_infringement' || action.violation_type === 'trademark_infringement';
    primaryCTA = { label: isIp ? t('app.actionQueue.ctaFileIpReport') : t('app.actionQueue.ctaOpenReport'), icon: FaExternalLinkAlt, color: '#ff453a', onPress: () => window.open(reportTarget, '_blank') };
  } else {
    const hasDraftable = isPost ? !!action.post_content : !!action.narrative_id;
    if ((action.recommended_action === 'react' || action.recommended_action === 'counter_narrative' || action.recommended_action === 'engage') && hasDraftable) {
      primaryCTA = { label: isPost ? t('app.actionQueue.ctaDraftReply') : t('app.actionQueue.ctaDraftPost'), icon: FaReply, color: T.primary, onPress: (e) => onDraft(action, e) };
      secondaryCTA = navCTA;
    } else {
      primaryCTA = navCTA;
      if (isPost && action.source_url && action.narrative_id) {
        secondaryCTA = { label: t('app.actionQueue.narrative'), icon: FaFire, color: T.warning, onPress: () => navigate(`/narratives/${action.narrative_id}`) };
      }
    }
  }

  const emotionColor = action.dominant_emotion ? (EMOTION_COLORS[action.dominant_emotion] || '#8e8e93') : null;

  // Parse narrative sample posts
  let samplePosts = [];
  if (action.narrative_sample_posts) {
    try { samplePosts = typeof action.narrative_sample_posts === 'string' ? JSON.parse(action.narrative_sample_posts) : action.narrative_sample_posts; }
    catch {}
  }

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

      {/* ── Main detail column ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0 }}>

        {/* ── SECTION 1: Header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 24 }}>
          <div style={{ width: 46, height: 46, borderRadius: 14, flexShrink: 0, background: `${sevColor}15`, border: `1.5px solid ${sevColor}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
            <IconComp size={19} color={isCompleted ? '#30d158' : sevColor} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: '0 0 10px', color: T.text, fontSize: 17, fontWeight: 700, letterSpacing: -0.4, lineHeight: '23px' }}>{action.title}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <span style={{ padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, background: `${sevColor}18`, color: sevColor }}>{action.severity}</span>
              <span style={{ color: T.textMuted, fontSize: 11 }}>·</span>
              <span style={{ color: T.textMuted, fontSize: 12, fontWeight: 500 }}>{actionLabel}</span>
              {action.dominant_emotion && emotionColor && (
                <>
                  <span style={{ color: T.textMuted, fontSize: 11 }}>·</span>
                  <span style={{ color: emotionColor, fontSize: 12, fontWeight: 500 }}>{action.dominant_emotion}</span>
                </>
              )}
              {timeAgo && (
                <>
                  <span style={{ color: T.textMuted, fontSize: 11 }}>·</span>
                  <span style={{ color: T.textMuted, fontSize: 12 }}>{timeAgo}</span>
                </>
              )}
            </div>
          </div>
          {!isCompleted && (
            <button onClick={() => onDismiss(action.id)} style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${T.border}`, background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
              <FaTimes size={10} color={T.textMuted} />
            </button>
          )}
        </div>

        {/* ── SECTION: Violation Detail ── */}
        {action.violation_detected && (
          <div style={{ marginBottom: 20, borderRadius: 12, border: '1.5px solid rgba(255,69,58,0.35)', background: 'rgba(255,69,58,0.05)', padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <FaFlag size={13} color="#ff453a" />
              <span style={{ color: '#ff453a', fontSize: 13, fontWeight: 700 }}>{t('app.actionQueue.violationTitle')}</span>
              {action.violation_confidence && (
                <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                  background: `${VIOLATION_CONFIDENCE_COLORS[action.violation_confidence] || '#8e8e93'}18`,
                  color: VIOLATION_CONFIDENCE_COLORS[action.violation_confidence] || '#8e8e93',
                }}>
                  {action.violation_confidence} {t('app.actionQueue.confidence')}
                </span>
              )}
            </div>
            {action.violation_type && (
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>
                  {VIOLATION_TYPE_LABELS[action.violation_type] || action.violation_type.replace(/_/g, ' ')}
                </span>
              </div>
            )}
            {action.violation_explanation && (
              <p style={{ margin: '0 0 8px', color: T.textSec, fontSize: 13, lineHeight: 1.6 }}>{action.violation_explanation}</p>
            )}
            {action.violation_rule_reference && (
              <p style={{ margin: '0 0 12px', color: T.textMuted, fontSize: 12, fontStyle: 'italic' }}>{action.violation_rule_reference}</p>
            )}
            {PLATFORM_REPORT_INSTRUCTIONS[action.source_type] && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(255,69,58,0.06)', border: '1px solid rgba(255,69,58,0.15)', marginBottom: 12 }}>
                <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{t('app.actionQueue.howToReport')}</div>
                <p style={{ margin: 0, color: T.textSec, fontSize: 12, lineHeight: 1.5 }}>{PLATFORM_REPORT_INSTRUCTIONS[action.source_type]}</p>
              </div>
            )}
            {action.report_url && (
              <button
                className="aq-cta"
                onClick={() => window.open(action.report_url, '_blank')}
                style={{ background: '#ff453a', color: '#fff', border: 'none', fontWeight: 600, boxShadow: '0 2px 12px rgba(255,69,58,0.35)' }}
              >
                <FaExternalLinkAlt size={11} color="#fff" />
                {action.violation_type === 'copyright_infringement' || action.violation_type === 'trademark_infringement' ? t('app.actionQueue.ctaFileIpReport') : t('app.actionQueue.ctaOpenReport')}
              </button>
            )}
          </div>
        )}

        {/* ── SECTION 2: Why this matters ── */}
        {action.reasoning && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: T.textMuted, marginBottom: 8 }}>{t('app.actionQueue.whyThisMatters')}</div>
            <p style={{ margin: 0, color: T.textSec, fontSize: 14, lineHeight: 1.7, fontWeight: 400 }}>{action.reasoning}</p>
          </div>
        )}

        {/* ── divider ── */}
        {action.reasoning && action.suggested_steps?.length > 0 && (
          <div style={{ height: 1, background: T.border, marginBottom: 20 }} />
        )}

        {/* ── SECTION 3: Recommended steps ── */}
        {action.suggested_steps?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: T.primary, marginBottom: 12 }}>{t('app.actionQueue.recommendedSteps')}</div>
            <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
              {action.suggested_steps.map((step, i) => {
                const stepMatch = step.match(/monitor|track|מעקב|נטר|עקוב|ניטור/i);
                const showStepTrack = !!stepMatch;
                const stepTrackLabel = stepMatch ? (/monitor|ניטור|נטר/i.test(stepMatch[0]) ? t('app.actionQueue.startMonitoring') : t('app.actionQueue.trackOutcome')) : '';
                return (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '13px 16px', borderBottom: i < action.suggested_steps.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                  <div style={{ width: 22, height: 22, borderRadius: 11, flexShrink: 0, border: `1.5px solid ${T.primary}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                    <span style={{ color: T.primary, fontSize: 10, fontWeight: 800 }}>{i + 1}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, color: T.text, fontSize: 13.5, lineHeight: '22px', flex: 1 }}>{step}</p>
                    {showStepTrack && trackingState !== 'done' && (
                      <button className="aq-cta" disabled={trackingState === 'loading'} onClick={async (e) => {
                        e.stopPropagation();
                        setTrackingState('loading');
                        try {
                          await ListsManager.createTracker({
                            tracker_type: 'action_outcome',
                            schedule_pattern: 'passive',
                            action_queue_id: action.id,
                            narrative_id: action.narrative_id || undefined,
                            source_post_id: action.source_post_id || undefined,
                            source_platform: action.source_type || undefined,
                            source_url: action.source_url || undefined,
                            title: `Tracking: ${(action.title || 'Action').slice(0, 80)}`,
                            org_id: activeOrg?.id || action.org_id || undefined,
                          });
                          setTrackingState('done');
                        } catch (err) {
                          console.error(err);
                          setTrackingState('error');
                          setTimeout(() => setTrackingState('idle'), 3000);
                        }
                      }} style={{
                        flexShrink: 0, padding: '4px 10px', fontSize: 11, borderRadius: 14,
                        background: 'transparent', border: '1px solid rgba(10,132,255,0.4)', color: '#0a84ff',
                        cursor: trackingState === 'loading' ? 'wait' : 'pointer', opacity: trackingState === 'loading' ? 0.6 : 1,
                      }}>
                        {trackingState === 'loading' ? (
                          <><ActivityIndicator size={8} color="#0a84ff" /> {t('app.actionQueue.trackingEllipsis')}</>
                        ) : (
                          <><FaBinoculars size={9} color="#0a84ff" /> {stepTrackLabel}</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── SECTION 4: Narrative metrics (if narrative source) ── */}
        {isNarrative && (
          <div style={{ marginBottom: 20 }}>
            <NarrativeMetricsBar action={action} navigate={navigate} T={T} />
          </div>
        )}

        {/* ── SECTION 5: Narrative sample posts grid ── */}
        {isNarrative && samplePosts.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Posts in this narrative</div>
            <NarrativeSampleGrid posts={samplePosts} narrativeId={action.narrative_id} T={T} />
          </div>
        )}

        {/* ── SECTION 6: Who posted this (post source metadata) ── */}
        {isPost && (
          <div style={{ marginBottom: 20 }}>
            <PostAuthorCard action={action} platColor={platColor} PlatformIcon={PlatformIcon} T={T} />
          </div>
        )}

        {/* ── SECTION 7: Team — Assignees + Work Status ── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: T.textMuted }}>{t('app.actionQueue.assignedTo')}</div>
            {!isCompleted && (
              <div style={{ position: 'relative' }}>
                <div onClick={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); if (isWsOpen) { setWorkStatusOpen(null); return; } const rect = e.currentTarget.getBoundingClientRect(); setWorkStatusOpen({ id: action.id, top: rect.bottom + 6, left: rect.left }); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px 4px 8px', borderRadius: 20, border: `1px solid ${T.border}`, background: wsConf.bg, cursor: 'pointer', userSelect: 'none' }}>
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: wsConf.dot, flexShrink: 0 }} />
                  <span style={{ color: wsConf.color, fontSize: 11, fontWeight: 600 }}>{t(wsConf.labelKey)}</span>
                  <span style={{ color: T.textMuted, fontSize: 9, marginLeft: 1 }}>▾</span>
                </div>
                {isWsOpen && ReactDOM.createPortal(
                  <div onClick={e => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }} style={{ position: 'fixed', top: workStatusOpen.top, left: workStatusOpen.left, zIndex: 99999, borderRadius: 10, overflow: 'hidden', background: T.elevated, border: `1px solid ${T.border}`, boxShadow: '0 12px 40px rgba(0,0,0,0.45)', minWidth: 148 }}>
                    {Object.entries(WS_CONFIG).map(([key, c]) => (
                      <div key={key} onClick={() => { onWorkStatusChange(action.id, key); }} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', cursor: 'pointer', background: ws === key ? `${c.dot}12` : 'transparent' }}
                        onMouseEnter={e => { if (ws !== key) e.currentTarget.style.background = T.surface; }}
                        onMouseLeave={e => { e.currentTarget.style.background = ws === key ? `${c.dot}12` : 'transparent'; }}>
                        <span style={{ width: 7, height: 7, borderRadius: 4, background: c.dot, flexShrink: 0 }} />
                        <span style={{ color: ws === key ? c.color : T.textSec, fontSize: 12, fontWeight: ws === key ? 700 : 400 }}>{t(c.labelKey)}</span>
                        {ws === key && <span style={{ color: c.color, fontSize: 10, marginLeft: 'auto' }}>✓</span>}
                      </div>
                    ))}
                  </div>,
                  document.body
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {action.assignees?.length > 0 ? action.assignees.map((a) => {
              const isSelf = a.listener_id === myListenerId;
              const canUnassign = isSelf || myRole === 'owner' || myRole === 'admin';
              const colorIdx = a.username ? a.username.charCodeAt(0) % AVATAR_COLORS.length : 0;
              return (
                <div key={a.listener_id} title={`${a.username || '?'}${canUnassign ? ` — ${t('app.actionQueue.clickToUnassign')}` : ''}`}
                  onClick={canUnassign ? () => onUnassign(action.id, a.listener_id) : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', borderRadius: 20, background: T.surface, border: `1px solid ${T.border}`, cursor: canUnassign ? 'pointer' : 'default' }}>
                  <div style={{ width: 20, height: 20, borderRadius: 10, background: AVATAR_COLORS[colorIdx], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#fff', fontSize: 8, fontWeight: 800 }}>{(a.username || '?')[0].toUpperCase()}</span>
                  </div>
                  <span style={{ color: T.textSec, fontSize: 12, fontWeight: 500 }}>{a.username?.split('@')[0] || '?'}</span>
                </div>
              );
            }) : <span style={{ color: T.textMuted, fontSize: 13 }}>{t('app.actionQueue.unassigned')}</span>}
            {(action.can_claim || action.can_assign) && !isCompleted && (
              <button className="aq-btn" onClick={(e) => action.can_assign ? onOpenAssign(action.id, e) : onClaim(action.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 20, background: 'transparent', border: `1px dashed ${T.border}`, color: T.textMuted, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                <FaUserPlus size={9} color={T.textMuted} />
                {action.can_assign ? t('app.actionQueue.assign') : t('app.actionQueue.takeIt')}
              </button>
            )}
          </div>
        </div>

        {/* ── SECTION 8: Action buttons ── */}
        <div style={{ height: 1, background: T.border, marginBottom: 20 }} />
        {!isCompleted ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {primaryCTA && (
              <button className="aq-cta" onClick={(e) => { e.stopPropagation(); primaryCTA.onPress(e); }}
                style={{ background: primaryCTA.color, border: 'none', color: '#fff', fontWeight: 600, boxShadow: `0 2px 12px ${primaryCTA.color}55` }}>
                <primaryCTA.icon size={12} color="#fff" />{primaryCTA.label}
              </button>
            )}
            {secondaryCTA && (
              <button className="aq-cta" onClick={(e) => { e.stopPropagation(); secondaryCTA.onPress(e); }} style={{ background: 'transparent', border: `1px solid ${T.border}`, color: T.textSec }}>
                <secondaryCTA.icon size={12} color={T.textMuted} />{secondaryCTA.label}
              </button>
            )}
            <button className="aq-cta" onClick={() => onShare(action)} style={{ background: 'transparent', border: `1px solid ${T.border}`, color: T.textSec }}>
              <FaShareAlt size={11} color={T.textMuted} />{t('app.actionQueue.share')}
            </button>
            {action.can_mark_done && (
              <button className="aq-cta" onClick={() => onMarkDone(action.id)} style={{ background: 'transparent', border: `1px solid rgba(48,209,88,0.4)`, color: '#30d158' }}>
                <FaCheckCircle size={11} color="#30d158" />{t('app.actionQueue.markDone')}
              </button>
            )}
            <button className="aq-cta" disabled={trackingState === 'loading' || trackingState === 'done'} onClick={async () => {
                setTrackingState('loading');
                try {
                  await ListsManager.createTracker({
                    tracker_type: (action.recommended_action || '').toLowerCase().includes('report') ? 'content_report' : 'action_outcome',
                    schedule_pattern: 'passive',
                    action_queue_id: action.id,
                    narrative_id: action.narrative_id || undefined,
                    source_post_id: action.source_post_id || undefined,
                    source_platform: action.source_type || undefined,
                    source_url: action.source_url || undefined,
                    title: `Tracking: ${(action.title || 'Action').slice(0, 80)}`,
                    org_id: activeOrg?.id || action.org_id || undefined,
                  });
                  setTrackingState('done');
                } catch (e) {
                  console.error(e);
                  setTrackingState('error');
                  setTimeout(() => setTrackingState('idle'), 3000);
                }
              }} style={{
                background: trackingState === 'done' ? 'rgba(48,209,88,0.12)' : trackingState === 'error' ? 'rgba(255,69,58,0.12)' : 'transparent',
                border: `1px solid ${trackingState === 'done' ? 'rgba(48,209,88,0.4)' : trackingState === 'error' ? 'rgba(255,69,58,0.4)' : 'rgba(10,132,255,0.4)'}`,
                color: trackingState === 'done' ? '#30d158' : trackingState === 'error' ? '#ff453a' : '#0a84ff',
                opacity: trackingState === 'loading' ? 0.6 : 1,
                cursor: trackingState === 'loading' ? 'wait' : trackingState === 'done' ? 'default' : 'pointer',
              }}>
                {trackingState === 'loading' ? (
                  <><ActivityIndicator size={10} color="#0a84ff" /> {t('app.actionQueue.trackingEllipsis')}</>
                ) : trackingState === 'done' ? (
                  <><FaCheckCircle size={11} color="#30d158" /> {t('app.actionQueue.tracked')}</>
                ) : trackingState === 'error' ? (
                  <><FaTimes size={11} color="#ff453a" /> {t('app.actionQueue.failedRetry')}</>
                ) : (
                  <><FaBinoculars size={11} color="#0a84ff" /> {t('app.actionQueue.trackOutcome')}</>
                )}
              </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaCheckCircle size={13} color="#30d158" />
              <span style={{ color: '#30d158', fontSize: 13, fontWeight: 600 }}>{t('app.actionQueue.completed')}</span>
              {action.acted_by_name && <span style={{ color: T.textMuted, fontSize: 12 }}>{t('app.actionQueue.by')} {action.acted_by_name}</span>}
            </div>
            <button className="aq-cta" onClick={() => onReopen(action.id)} style={{ background: 'transparent', border: `1px solid ${T.border}`, color: T.textSec }}>
              <FaUndo size={11} color={T.textMuted} />{t('app.actionQueue.reopen')}
            </button>
            <button className="aq-cta" disabled={trackingState === 'loading' || trackingState === 'done'} onClick={async () => {
              setTrackingState('loading');
              try {
                await ListsManager.createTracker({
                  tracker_type: (action.recommended_action || '').toLowerCase().includes('report') ? 'content_report' : 'action_outcome',
                  action_queue_id: action.id,
                  narrative_id: action.narrative_id || undefined,
                  source_post_id: action.source_post_id || undefined,
                  source_platform: action.source_type || undefined,
                  source_url: action.source_url || undefined,
                  title: `Tracking: ${(action.title || 'Action').slice(0, 80)}`,
                  org_id: activeOrg?.id || action.org_id || undefined,
                });
                setTrackingState('done');
              } catch (e) {
                console.error(e);
                setTrackingState('error');
                setTimeout(() => setTrackingState('idle'), 3000);
              }
            }} style={{
              background: trackingState === 'done' ? 'rgba(48,209,88,0.12)' : trackingState === 'error' ? 'rgba(255,69,58,0.12)' : 'transparent',
              border: `1px solid ${trackingState === 'done' ? 'rgba(48,209,88,0.4)' : trackingState === 'error' ? 'rgba(255,69,58,0.4)' : 'rgba(10,132,255,0.4)'}`,
              color: trackingState === 'done' ? '#30d158' : trackingState === 'error' ? '#ff453a' : '#0a84ff',
              opacity: trackingState === 'loading' ? 0.6 : 1,
              cursor: trackingState === 'loading' ? 'wait' : trackingState === 'done' ? 'default' : 'pointer',
            }}>
              {trackingState === 'loading' ? (
                <><ActivityIndicator size={10} color="#0a84ff" /> {t('app.actionQueue.trackingEllipsis')}</>
              ) : trackingState === 'done' ? (
                <><FaCheckCircle size={11} color="#30d158" /> {t('app.actionQueue.tracked')}</>
              ) : trackingState === 'error' ? (
                <><FaTimes size={11} color="#ff453a" /> {t('app.actionQueue.failedRetry')}</>
              ) : (
                <><FaBinoculars size={11} color="#0a84ff" /> {t('app.actionQueue.trackOutcome')}</>
              )}
            </button>
          </div>
        )}
      </div>

      {/* ── RIGHT: media/content pane ── */}
      <MediaPane action={action} tiktokId={tiktokId} tweetId={tweetId} platColor={platColor} T={T} navigate={navigate} />
    </div>
  );
}

// ── PostAuthorCard — "Who posted this?" ───────────────────────────────────

function PostAuthorCard({ action, platColor, PlatformIcon, T }) {
  const { t } = useTranslation();
  if (!action.post_author) return null;

  const followerVal = action.post_author_followers;
  const influenceLevel = followerVal >= 1000000 ? { label: 'Mega Influencer', color: '#bf5af2' }
    : followerVal >= 100000 ? { label: 'Macro Influencer', color: '#ff9f0a' }
    : followerVal >= 10000  ? { label: 'Mid-tier', color: '#0a84ff' }
    : followerVal >= 1000   ? { label: 'Micro', color: '#30d158' }
    : { label: 'Nano', color: '#8e8e93' };

  return (
    <div>
      <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{t('app.actionQueue.whoPostedThis')}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 12, background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${platColor}` }}>
        {action.post_author_profile_image ? (
          <img src={action.post_author_profile_image} alt="" style={{ width: 48, height: 48, borderRadius: 24, objectFit: 'cover', flexShrink: 0, border: `2px solid ${platColor}40` }} />
        ) : (
          <div style={{ width: 48, height: 48, borderRadius: 24, background: `${platColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {PlatformIcon && <PlatformIcon size={20} color={platColor} />}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>@{action.post_author}</span>
            {PlatformIcon && <PlatformIcon size={13} color={platColor} />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {followerVal > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <FaUsers size={11} color={T.textMuted} />
                <span style={{ color: T.textSec, fontSize: 12, fontWeight: 600 }}>{fmtNum(followerVal)} {t('app.actionQueue.followers')}</span>
              </div>
            )}
            <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: `${influenceLevel.color}18`, color: influenceLevel.color }}>{influenceLevel.label}</span>
          </div>
        </div>
        {action.source_url && (
          <a href={action.source_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 5, color: platColor, fontSize: 12, fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}>
            <FaExternalLinkAlt size={10} />
            View
          </a>
        )}
      </div>
    </div>
  );
}

// ── NarrativeMetricsBar ────────────────────────────────────────────────────

function NarrativeMetricsBar({ action, navigate, T }) {
  const { t } = useTranslation();
  const vel = action.narrative_velocity_1h || action.velocity_1h || 0;
  const isTrending = action.narrative_is_trending;
  const msgCount = action.message_count || 0;
  const sentiment = action.sentiment_score;

  return (
    <div style={{ borderRadius: 12, padding: '14px 16px', background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.warning}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <FaFire size={14} color={T.warning} />
        <span style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>{action.narrative_title || t('app.actionQueue.narrative')}</span>
        {isTrending && (
          <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#ff453a18', color: '#ff453a' }}>{t('app.actionQueue.trending')}</span>
        )}
        {action.narrative_id && (
          <button onClick={() => navigate(`/narratives/${action.narrative_id}`)} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: T.warning, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
            View <FaArrowRight size={10} color={T.warning} />
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.actionQueue.posts')}</span>
          <span style={{ color: T.text, fontSize: 16, fontWeight: 700 }}>{fmtNum(msgCount)}</span>
        </div>
        {vel > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.actionQueue.velocity')}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <FaArrowUp size={10} color={T.warning} />
              <span style={{ color: T.warning, fontSize: 16, fontWeight: 700 }}>{vel.toFixed(1)}/h</span>
            </div>
          </div>
        )}
        {sentiment != null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.actionQueue.sentiment')}</span>
            <span style={{ color: sentiment >= 0.3 ? '#30d158' : sentiment <= -0.3 ? '#ff453a' : '#8e8e93', fontSize: 16, fontWeight: 700 }}>
              {sentiment >= 0 ? '+' : ''}{(sentiment * 100).toFixed(0)}%
            </span>
          </div>
        )}
      </div>
      {action.narrative_summary && (
        <p style={{ margin: '10px 0 0', color: T.textSec, fontSize: 13, lineHeight: 1.6 }}>{action.narrative_summary}</p>
      )}
    </div>
  );
}

// ── NarrativeSampleGrid ────────────────────────────────────────────────────

function NarrativeSampleGrid({ posts: initialPosts, narrativeId, T }) {
  const { t } = useTranslation();
  const [allPosts, setAllPosts]   = useState(initialPosts || []);
  const [loading, setLoading]     = useState(false);
  const [hasMore, setHasMore]     = useState(initialPosts?.length >= 5);
  const [offset, setOffset]       = useState(initialPosts?.length || 0);

  const loadMore = async () => {
    if (loading || !narrativeId) return;
    setLoading(true);
    const result = await ListsManager.fetchNarrativePosts(narrativeId, 20, offset);
    if (result?.posts?.length > 0) {
      setAllPosts(prev => [...prev, ...result.posts]);
      setOffset(prev => prev + result.posts.length);
      setHasMore(result.has_more);
    } else {
      setHasMore(false);
    }
    setLoading(false);
  };

  // Split posts into 2 columns for masonry effect
  const col1 = allPosts.filter((_, i) => i % 2 === 0);
  const col2 = allPosts.filter((_, i) => i % 2 === 1);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[col1, col2].map((col, ci) => (
          <div key={ci} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {col.map((p, i) => <NarrativePostTile key={`${ci}-${i}`} p={p} T={T} />)}
          </div>
        ))}
      </div>
      {(hasMore || loading) && (
        <button
          onClick={loadMore}
          disabled={loading}
          style={{ marginTop: 10, width: '100%', padding: '9px 0', borderRadius: 10, background: T.surface, border: `1px solid ${T.border}`, color: loading ? T.textMuted : T.primary, fontSize: 12, fontWeight: 600, cursor: loading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {loading ? <><ActivityIndicator size="small" color={T.primary} />Loading…</> : t('app.actionQueue.loadMorePosts')}
        </button>
      )}
    </div>
  );
}

function NarrativePostTile({ p, T }) {
  const platColor = PLATFORM_COLORS[p.source_type] || '#8e8e93';
  const PlatIcon = PLATFORM_ICONS[p.source_type] || FaGlobe;
  const likes = p.likes || 0;
  const views = p.views || 0;
  const reposts = p.reposts || 0;
  const replies = p.replies || 0;
  const avatarIdx = p.author_username ? p.author_username.charCodeAt(0) % AVATAR_COLORS.length : 0;

  return (
    <div
      className="aq-smp-card"
      style={{ background: T.card, border: `1px solid ${T.border}`, padding: '12px 12px 10px' }}
      onClick={() => p.post_url && window.open(p.post_url, '_blank')}
    >
      {/* author row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: 14, background: AVATAR_COLORS[avatarIdx] + '40', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1.5px solid ${platColor}30` }}>
          <span style={{ color: AVATAR_COLORS[avatarIdx], fontSize: 11, fontWeight: 800 }}>{(p.author_username || '?')[0].toUpperCase()}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: T.text, fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{p.author_username || 'unknown'}</div>
          {p.author_followers > 0 && <div style={{ color: T.textMuted, fontSize: 10 }}>{fmtNum(p.author_followers)}</div>}
        </div>
        {/* platform badge */}
        <div style={{ width: 22, height: 22, borderRadius: 11, background: `${platColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${platColor}30` }}>
          <PlatIcon size={11} color={platColor} />
        </div>
      </div>

      {/* content */}
      <p style={{ margin: 0, color: T.textSec, fontSize: 12, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 8 }}>{p.content}</p>

      {/* stats row */}
      {(likes > 0 || views > 0 || reposts > 0 || replies > 0) && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {views > 0 && <Stat icon={FaEye} val={fmtNum(views)} color={T.textMuted} T={T} />}
          {likes > 0 && <Stat icon={FaHeart} val={fmtNum(likes)} color="#ff453a" T={T} />}
          {reposts > 0 && <Stat icon={FaRetweet} val={fmtNum(reposts)} color="#30d158" T={T} />}
          {replies > 0 && <Stat icon={FaComment} val={fmtNum(replies)} color={T.textMuted} T={T} />}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, val, color, T }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <Icon size={10} color={color} />
      <span style={{ color: T.textMuted, fontSize: 11 }}>{val}</span>
    </div>
  );
}

// ── Expandable caption ────────────────────────────────────────────────────

function ExpandableCaption({ text, style, clampLines = 3 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  return (
    <p
      onClick={() => setExpanded(e => !e)}
      style={{
        ...style,
        cursor: 'pointer',
        ...(expanded ? { display: 'block' } : { display: '-webkit-box', WebkitLineClamp: clampLines, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
      }}
    >{text}</p>
  );
}

// ── MediaPane (right side — rich media preview) ───────────────────────────

function MediaPane({ action, tiktokId, tweetId, platColor, T, navigate }) {
  const { t } = useTranslation();
  const isPost      = action.source_kind === 'post';
  const isTikTok    = action.source_type === 'tiktok';
  const isTwitter   = action.source_type === 'twitter';
  const isTelegram  = action.source_type === 'telegram';
  const isInstagram = action.source_type === 'instagram';
  const isNarrative = action.source_kind === 'narrative';
  const isCampaign = action.source_kind === 'campaign_post';
  const PlatformIcon = PLATFORM_ICONS[action.source_type];

  const hasContent = isPost || isNarrative || isCampaign;
  if (!hasContent) return null;

  return (
    <div style={{ width: 320, flexShrink: 0, borderLeft: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {isNarrative ? t('app.actionQueue.narrativeContext') : isCampaign ? t('app.actionQueue.campaignPost') : t('app.actionQueue.sourceContent')}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* ── TIKTOK embed ── */}
        {isTikTok && tiktokId && (
          <div className="aq-tiktok-wrap" style={{ borderRadius: 12, overflow: 'hidden', background: '#111', border: `1px solid ${T.border}` }}>
            <iframe
              src={`https://www.tiktok.com/embed/v2/${tiktokId}`}
              style={{ width: '100%', height: 500, border: 'none' }}
              allowFullScreen
              allow="encrypted-media; autoplay"
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            />
            {/* Engagement stats */}
            <div style={{ padding: '10px 14px', borderTop: `1px solid ${T.border}`, background: T.card }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <FaTiktok size={12} color="#ff0050" />
                <span style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>@{action.post_author}</span>
                <span style={{ color: T.textMuted, fontSize: 11, marginLeft: 'auto' }}>{fmtNum(action.post_author_followers)} {t('app.actionQueue.followers')}</span>
              </div>
              <ExpandableCaption text={action.post_content} clampLines={5} style={{ margin: 0, color: T.textSec, fontSize: 12, lineHeight: 1.5 }} />
            </div>
          </div>
        )}

        {/* ── TikTok — no embed (no ID) fallback ── */}
        {isTikTok && !tiktokId && action.post_content && (
          <TikTokCard action={action} T={T} />
        )}

        {/* ── Twitter card ── */}
        {isTwitter && (
          <TwitterCard action={action} tweetId={tweetId} T={T} />
        )}

        {/* ── Telegram card ── */}
        {isTelegram && action.post_content && (
          <TelegramCard action={action} T={T} />
        )}

        {/* ── Instagram card ── */}
        {isInstagram && (
          <InstagramCard action={action} T={T} />
        )}

        {/* ── RSS / generic post ── */}
        {!isTikTok && !isTwitter && !isTelegram && !isInstagram && isPost && action.post_content && (
          <GenericPostCard action={action} platColor={platColor} PlatformIcon={PlatformIcon} T={T} />
        )}

        {/* ── Narrative source context ── */}
        {isNarrative && (
          <NarrativeContextCard action={action} T={T} navigate={navigate} />
        )}

        {/* ── Campaign post ── */}
        {isCampaign && (
          <CampaignMediaSection action={action} T={T} navigate={navigate} />
        )}
      </div>
    </div>
  );
}

// ── TikTok card (no embed) ─────────────────────────────────────────────────

function TikTokCard({ action, T }) {
  const { t } = useTranslation();
  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', background: '#111', border: `1px solid rgba(255,0,80,0.2)` }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(255,0,80,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {action.post_author_profile_image ? (
            <img src={action.post_author_profile_image} alt="" style={{ width: 38, height: 38, borderRadius: 19, objectFit: 'cover', border: '2px solid rgba(255,0,80,0.4)' }} />
          ) : (
            <div style={{ width: 38, height: 38, borderRadius: 19, background: 'rgba(255,0,80,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaTiktok size={18} color="#ff0050" />
            </div>
          )}
          <div>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>@{action.post_author}</div>
            {action.post_author_followers > 0 && <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{fmtNum(action.post_author_followers)} {t('app.actionQueue.followers')}</div>}
          </div>
          <FaTiktok size={14} color="#ff0050" style={{ marginLeft: 'auto' }} />
        </div>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.6 }}>{action.post_content}</p>
        {action.source_url && (
          <a href={action.source_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 12, color: '#ff0050', fontSize: 12, fontWeight: 600, textDecoration: 'none', padding: '7px 14px', borderRadius: 20, background: 'rgba(255,0,80,0.12)', border: '1px solid rgba(255,0,80,0.25)' }}>
            <FaExternalLinkAlt size={10} />{t('app.actionQueue.watchOnTikTok')}
          </a>
        )}
      </div>
    </div>
  );
}

// ── Twitter card ───────────────────────────────────────────────────────────

function TwitterCard({ action, tweetId, T }) {
  const { t } = useTranslation();
  return (
    <div style={{ borderRadius: 16, background: T.card, border: `1px solid ${T.border}`, borderLeft: '3px solid #1da1f2', overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px' }}>
        {/* author row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          {action.post_author_profile_image ? (
            <img src={action.post_author_profile_image} alt="" style={{ width: 40, height: 40, borderRadius: 20, objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 40, height: 40, borderRadius: 20, background: 'rgba(29,161,242,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaTwitter size={18} color="#1da1f2" />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>@{action.post_author}</div>
            {action.post_author_followers > 0 && <div style={{ color: T.textMuted, fontSize: 11 }}>{fmtNum(action.post_author_followers)} {t('app.actionQueue.followers')}</div>}
          </div>
          <FaTwitter size={16} color="#1da1f2" />
        </div>
        {/* content */}
        {action.post_content && (
          <p style={{ margin: '0 0 12px', color: T.text, fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{action.post_content}</p>
        )}
        {/* actions row */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {action.source_url && (
            <a href={action.source_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#1da1f2', fontSize: 12, fontWeight: 600, textDecoration: 'none', padding: '6px 12px', borderRadius: 20, background: 'rgba(29,161,242,0.1)', border: '1px solid rgba(29,161,242,0.25)' }}>
              <FaExternalLinkAlt size={9} />{t('app.actionQueue.viewOnX')}
            </a>
          )}
          {tweetId && (
            <a href={`https://twitter.com/intent/reply?in_reply_to=${tweetId}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: T.textSec, fontSize: 12, fontWeight: 600, textDecoration: 'none', padding: '6px 12px', borderRadius: 20, background: T.surface, border: `1px solid ${T.border}` }}>
              <FaReply size={9} />{t('app.actionQueue.reply')}
            </a>
          )}
          {tweetId && (
            <a href={`https://twitter.com/intent/retweet?tweet_id=${tweetId}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#30d158', fontSize: 12, fontWeight: 600, textDecoration: 'none', padding: '6px 12px', borderRadius: 20, background: 'rgba(48,209,88,0.1)', border: '1px solid rgba(48,209,88,0.25)' }}>
              <FaRetweet size={9} />{t('app.actionQueue.retweet')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Telegram card ──────────────────────────────────────────────────────────

function TelegramCard({ action, T }) {
  const { t } = useTranslation();
  return (
    <div style={{ borderRadius: 18, borderBottomRightRadius: 4, background: 'rgba(33,47,61,0.6)', border: '1px solid rgba(51,144,236,0.2)', overflow: 'hidden' }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(51,144,236,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(0,136,204,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FaTelegram size={18} color="#0088cc" />
          </div>
          <div>
            <div style={{ color: '#3390ec', fontSize: 14, fontWeight: 700 }}>{action.post_author}</div>
            {action.post_author_followers > 0 && <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{fmtNum(action.post_author_followers)} subscribers</div>}
          </div>
        </div>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)', fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{action.post_content}</p>
        {action.source_url && (
          <a href={action.source_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 12, color: '#0088cc', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
            <FaExternalLinkAlt size={10} />{t('app.actionQueue.viewOnTelegram')}
          </a>
        )}
      </div>
    </div>
  );
}

// ── Instagram card ─────────────────────────────────────────────────────────

function InstagramCard({ action, T }) {
  const { t } = useTranslation();
  const shortcode = (action.source_url || '').match(/instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/)?.[1] || null;

  if (shortcode) {
    return (
      <div style={{ borderRadius: 14, overflow: 'hidden', background: '#000', border: '1px solid rgba(225,48,108,0.25)' }}>
        <iframe
          src={`https://www.instagram.com/p/${shortcode}/embed/`}
          style={{ width: '100%', height: 480, border: 'none', display: 'block' }}
          allowFullScreen
          scrolling="no"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      </div>
    );
  }

  // Fallback: custom card when no embeddable URL
  const images = action.post_images || action.tweet_images || [];
  const igGradient = 'linear-gradient(135deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)';

  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', background: T.card, border: '1px solid rgba(225,48,108,0.25)' }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(225,48,108,0.15)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 18, padding: 2, background: igGradient, flexShrink: 0 }}>
          <div style={{ width: '100%', height: '100%', borderRadius: 16, background: T.card, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FaInstagram size={16} color="#e1306c" />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>@{action.post_author || 'instagram'}</div>
          {action.post_author_followers > 0 && (
            <div style={{ color: T.textMuted, fontSize: 11 }}>{fmtNum(action.post_author_followers)} {t('app.actionQueue.followers')}</div>
          )}
        </div>
        <FaInstagram size={16} color="#e1306c" />
      </div>
      {images.length > 0 && (
        <div style={{ width: '100%', aspectRatio: '1', overflow: 'hidden', background: '#000' }}>
          <img src={images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => { e.target.parentNode.style.display = 'none'; }} />
        </div>
      )}
      <div style={{ padding: '12px 14px' }}>
        <ExpandableCaption text={action.post_content} clampLines={8} style={{ margin: '0 0 10px', color: T.text, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }} />
        {action.source_url && (
          <a href={action.source_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#e1306c', fontSize: 12, fontWeight: 600, textDecoration: 'none', padding: '6px 12px', borderRadius: 20, background: 'rgba(225,48,108,0.1)', border: '1px solid rgba(225,48,108,0.25)' }}>
            <FaExternalLinkAlt size={9} />{t('app.actionQueue.viewOnInstagram')}
          </a>
        )}
      </div>
    </div>
  );
}

// ── Generic post card ──────────────────────────────────────────────────────

function GenericPostCard({ action, platColor, PlatformIcon, T }) {
  const { t } = useTranslation();
  return (
    <div style={{ borderRadius: 12, background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${platColor}`, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px' }}>
        {action.post_author && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            {action.post_author_profile_image && <img src={action.post_author_profile_image} alt="" style={{ width: 32, height: 32, borderRadius: 16, objectFit: 'cover' }} />}
            <span style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>@{action.post_author}</span>
            {PlatformIcon && <PlatformIcon size={13} color={platColor} style={{ marginLeft: 'auto' }} />}
          </div>
        )}
        <p style={{ margin: 0, color: T.textSec, fontSize: 13, lineHeight: 1.6 }}>{action.post_content}</p>
        {action.source_url && (
          <a href={action.source_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 10, color: platColor, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
            <FaExternalLinkAlt size={10} />{t('app.actionQueue.viewOriginal')}
          </a>
        )}
      </div>
    </div>
  );
}

// ── Narrative context card (right pane) ────────────────────────────────────

function NarrativeContextCard({ action, T, navigate }) {
  const { t } = useTranslation();
  const vel = action.narrative_velocity_1h || action.velocity_1h || 0;
  const sentiment = action.sentiment_score;
  const emotionColor = action.dominant_emotion ? (EMOTION_COLORS[action.dominant_emotion] || '#8e8e93') : null;

  return (
    <div style={{ borderRadius: 12, background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.warning}`, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <FaFire size={15} color={T.warning} />
          <span style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>{action.narrative_title || t('app.actionQueue.narrative')}</span>
          {action.narrative_is_trending && <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: '#ff453a18', color: '#ff453a' }}>{t('app.actionQueue.trending')}</span>}
        </div>

        {/* Stats row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${T.border}` }}>
          {action.message_count > 0 && (
            <div>
              <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.actionQueue.posts')}</div>
              <div style={{ color: T.text, fontSize: 18, fontWeight: 700 }}>{fmtNum(action.message_count)}</div>
            </div>
          )}
          {vel > 0 && (
            <div>
              <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.actionQueue.velocity')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <FaArrowUp size={10} color={T.warning} />
                <span style={{ color: T.warning, fontSize: 18, fontWeight: 700 }}>{vel.toFixed(1)}/h</span>
              </div>
            </div>
          )}
          {sentiment != null && (
            <div>
              <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('app.actionQueue.sentiment')}</div>
              <div style={{ color: sentiment >= 0.3 ? '#30d158' : sentiment <= -0.3 ? '#ff453a' : '#8e8e93', fontSize: 18, fontWeight: 700 }}>
                {sentiment >= 0 ? '+' : ''}{(sentiment * 100).toFixed(0)}%
              </div>
            </div>
          )}
        </div>

        {/* Dominant emotion */}
        {action.dominant_emotion && emotionColor && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <span style={{ color: T.textMuted, fontSize: 12 }}>{t('app.actionQueue.dominantEmotion')}</span>
            <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${emotionColor}18`, color: emotionColor }}>{action.dominant_emotion}</span>
          </div>
        )}

        {/* Narrative summary */}
        {action.narrative_summary && (
          <p style={{ margin: '0 0 12px', color: T.textSec, fontSize: 13, lineHeight: 1.6 }}>{action.narrative_summary}</p>
        )}

        {action.narrative_id && (
          <button onClick={() => navigate(`/narratives/${action.narrative_id}`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: T.warning, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
            {t('app.actionQueue.deepDive')} <FaArrowRight size={10} color={T.warning} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── CampaignMediaSection ────────────────────────────────────────────────────

function CampaignMediaSection({ action, T, navigate }) {
  const { t } = useTranslation();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!action.campaign_id) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setCampaign(null);
    ListsManager.fetchCampaignDetail(action.campaign_id).then(data => {
      if (!cancelled && data) setCampaign(data);
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [action.campaign_id]);

  const posts = campaign?.posts || [];
  const triggerPost = action.source_post_id
    ? posts.find(p => p.id === action.source_post_id) || posts[0]
    : posts[0];
  const otherPosts = posts.filter(p => p.id !== triggerPost?.id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Campaign header */}
      <div style={{ borderRadius: 12, padding: '12px 14px', background: `${T.purple}10`, border: `1px solid ${T.purple}25` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: `${T.purple}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FaBullhorn size={13} color={T.purple} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: T.text, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {campaign?.name || 'Campaign'}
            </div>
            <div style={{ color: T.textMuted, fontSize: 11 }}>{posts.length} {t('app.actionQueue.postsTracked')}</div>
          </div>
          {action.campaign_id && (
            <button onClick={() => navigate(`/campaigns/${action.campaign_id}`)} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: T.purple, fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0, flexShrink: 0 }}>
              Open <FaArrowRight size={9} color={T.purple} />
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
          <ActivityIndicator size="small" color={T.purple} />
        </div>
      )}

      {/* Featured trigger post */}
      {!loading && triggerPost && (
        <CampaignPostFeatured post={triggerPost} T={T} />
      )}

      {/* Other posts grid */}
      {!loading && otherPosts.length > 0 && (
        <div>
          <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('app.actionQueue.allCampaignPosts')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {otherPosts.slice(0, 8).map((p, i) => (
              <CampaignPostTile key={p.id || i} post={p} T={T} />
            ))}
          </div>
          {posts.length > 9 && (
            <button onClick={() => navigate(`/campaigns/${action.campaign_id}`)} style={{ marginTop: 8, width: '100%', padding: '8px 0', borderRadius: 8, background: T.surface, border: `1px solid ${T.border}`, color: T.purple, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              View all {posts.length} posts →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── CampaignPostFeatured ────────────────────────────────────────────────────

function CampaignPostFeatured({ post, T }) {
  const { t } = useTranslation();
  const platform = post.platform || 'twitter';
  const platColor = PLATFORM_COLORS[platform] || '#8e8e93';
  const isTk = platform === 'tiktok';
  const isTw = platform === 'twitter';
  const isIg = platform === 'instagram';
  const isTg = platform === 'telegram';
  const tiktokId = post.platform_post_id || (post.post_url ? (post.post_url.match(/video\/(\d+)/) || [])[1] : null);
  const tweetId = post.platform_post_id || (post.post_url ? (post.post_url.match(/\/status\/(\d+)/) || [])[1] : null);

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${platColor}30` }}>
      {isTk && tiktokId && (
        <>
          <iframe
            src={`https://www.tiktok.com/embed/v2/${tiktokId}`}
            style={{ width: '100%', height: 420, border: 'none', display: 'block' }}
            allowFullScreen allow="encrypted-media; autoplay"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          />
          <div style={{ padding: '10px 14px', background: '#111', borderTop: '1px solid rgba(255,0,80,0.15)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaTiktok size={12} color="#ff0050" />
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>@{post.author_username}</span>
              <div style={{ display: 'flex', gap: 10, marginLeft: 'auto' }}>
                {post.latest_likes > 0 && <Stat icon={FaHeart} val={fmtNum(post.latest_likes)} color="#ff453a" T={T} />}
                {post.latest_views > 0 && <Stat icon={FaEye} val={fmtNum(post.latest_views)} color="rgba(255,255,255,0.4)" T={T} />}
              </div>
            </div>
          </div>
        </>
      )}
      {isTk && !tiktokId && (
        <div style={{ background: '#111', padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <FaTiktok size={14} color="#ff0050" />
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>@{post.author_username}</span>
          </div>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: 13, lineHeight: 1.6 }}>{post.content}</p>
          {post.post_url && <a href={post.post_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 10, color: '#ff0050', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}><FaExternalLinkAlt size={9} />{t('app.actionQueue.watchOnTikTok')}</a>}
        </div>
      )}
      {isTw && (
        <div style={{ background: T.card, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(29,161,242,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaTwitter size={16} color="#1da1f2" />
            </div>
            <div style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>@{post.author_username}</div>
            <FaTwitter size={14} color="#1da1f2" style={{ marginLeft: 'auto' }} />
          </div>
          {post.content && <p style={{ margin: '0 0 10px', color: T.text, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{post.content}</p>}
          <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            <Stat icon={FaHeart} val={fmtNum(post.latest_likes)} color="#ff453a" T={T} />
            <Stat icon={FaComment} val={fmtNum(post.latest_comments)} color={T.textMuted} T={T} />
            <Stat icon={FaRetweet} val={fmtNum(post.latest_shares)} color="#30d158" T={T} />
            <Stat icon={FaEye} val={fmtNum(post.latest_views)} color={T.textMuted} T={T} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {post.post_url && <a href={post.post_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1da1f2', fontSize: 12, fontWeight: 600, textDecoration: 'none', padding: '5px 10px', borderRadius: 18, background: 'rgba(29,161,242,0.1)', border: '1px solid rgba(29,161,242,0.2)' }}><FaExternalLinkAlt size={9} />{t('app.actionQueue.viewOnX')}</a>}
            {tweetId && <a href={`https://twitter.com/intent/reply?in_reply_to=${tweetId}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: T.textSec, fontSize: 12, fontWeight: 600, textDecoration: 'none', padding: '5px 10px', borderRadius: 18, background: T.surface, border: `1px solid ${T.border}` }}><FaReply size={9} />{t('app.actionQueue.reply')}</a>}
          </div>
        </div>
      )}
      {isIg && (
        <div style={{ background: T.card }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 10px' }}>
            <div style={{ width: 34, height: 34, borderRadius: 17, padding: 2, background: 'linear-gradient(135deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)' }}>
              <div style={{ width: '100%', height: '100%', background: T.card, borderRadius: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaInstagram size={14} color="#e1306c" />
              </div>
            </div>
            <span style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>@{post.author_username}</span>
          </div>
          {post.media_urls?.length > 0 && (
            <div style={{ width: '100%', aspectRatio: '1', overflow: 'hidden', background: '#000' }}>
              <img src={post.media_urls[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
          <div style={{ padding: '10px 14px' }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
              <Stat icon={FaHeart} val={fmtNum(post.latest_likes)} color="#ed4956" T={T} />
              <Stat icon={FaComment} val={fmtNum(post.latest_comments)} color={T.textMuted} T={T} />
            </div>
            {post.content && <p style={{ margin: 0, color: T.textSec, fontSize: 12, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.content}</p>}
          </div>
        </div>
      )}
      {isTg && (
        <div style={{ background: 'rgba(33,47,61,0.7)' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(51,144,236,0.12)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 16, background: 'rgba(0,136,204,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaTelegram size={15} color="#0088cc" />
            </div>
            <span style={{ color: '#3390ec', fontSize: 13, fontWeight: 700 }}>@{post.author_username}</span>
            {post.latest_views > 0 && <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, marginLeft: 'auto' }}>{fmtNum(post.latest_views)} views</span>}
          </div>
          <div style={{ padding: '12px 14px' }}>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.82)', fontSize: 13, lineHeight: 1.6 }}>{post.content}</p>
            {post.post_url && <a href={post.post_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, color: '#0088cc', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}><FaExternalLinkAlt size={9} />{_t('app.actionQueue.openInTelegram')}</a>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── CampaignPostTile ────────────────────────────────────────────────────────

function CampaignPostTile({ post, T }) {
  const platform = post.platform || 'twitter';
  const platColor = PLATFORM_COLORS[platform] || '#8e8e93';
  const PlatIcon = PLATFORM_ICONS[platform] || FaGlobe;
  const isTk = platform === 'tiktok';
  const tiktokId = post.platform_post_id || (post.post_url ? (post.post_url.match(/video\/(\d+)/) || [])[1] : null);

  return (
    <div
      className="aq-smp-card"
      style={{ background: isTk ? '#111' : T.card, border: `1px solid ${platColor}20`, padding: 0, overflow: 'hidden' }}
      onClick={() => post.post_url && window.open(post.post_url, '_blank')}
    >
      {isTk && tiktokId && (
        <iframe
          src={`https://www.tiktok.com/embed/v2/${tiktokId}`}
          style={{ width: '100%', height: 240, border: 'none', display: 'block', pointerEvents: 'none' }}
          allowFullScreen allow="encrypted-media"
          sandbox="allow-scripts allow-same-origin"
        />
      )}
      <div style={{ padding: '9px 12px', display: 'flex', alignItems: 'flex-start', gap: 9 }}>
        <div style={{ width: 26, height: 26, borderRadius: 13, background: `${platColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
          <PlatIcon size={11} color={platColor} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: isTk ? '#fff' : T.text, fontSize: 12, fontWeight: 600, marginBottom: 3 }}>@{post.author_username}</div>
          {post.content && (
            <p style={{ margin: 0, color: isTk ? 'rgba(255,255,255,0.65)' : T.textSec, fontSize: 11, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.content}</p>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 5 }}>
            {post.latest_likes > 0 && <Stat icon={FaHeart} val={fmtNum(post.latest_likes)} color="#ff453a" T={T} />}
            {post.latest_comments > 0 && <Stat icon={FaComment} val={fmtNum(post.latest_comments)} color={T.textMuted} T={T} />}
            {post.latest_views > 0 && <Stat icon={FaEye} val={fmtNum(post.latest_views)} color={T.textMuted} T={T} />}
            {post.latest_shares > 0 && <Stat icon={FaRetweet} val={fmtNum(post.latest_shares)} color="#30d158" T={T} />}
          </div>
        </div>
      </div>
    </div>
  );
}
